<script setup lang="ts">
import { WebsiteApiHelper } from '@/helpers/global/baseAPI.helper';
import DraggableHelper from '@/helpers/global/draggable.helper';
import GlobalHelper from '@/helpers/global/global.helper';
import GlobalPreloaderHelper from '@/helpers/global/globalPreloader.helper';
import VariablesHelper from '@/helpers/global/variables.helper';
import AnalyticsHelper from '@/helpers/global/analytics.helper';
import { onMounted } from 'vue';

// Button to top logic
let buttonToTopElement: HTMLElement = null;

const initButtonToTopElement = () => {
  buttonToTopElement = document.querySelector('.website-button-to-top');
};

const buttonToTopOnScrollHandler = () => {
  if (buttonToTopElement !== null) {
    if (document.documentElement.scrollTop > 200) {
      buttonToTopElement.classList.add('!translate-x-0', 'mr-2');
    } else {
      buttonToTopElement.classList.remove('!translate-x-0', 'mr-2');
    }
  }
};

const buttonToTopClickHandler = () => {
  if (buttonToTopElement !== null) {
    buttonToTopElement.addEventListener('click', () => {
      window.scrollTo({
        top: 0,
        behavior: 'smooth',
      });
    });
  }
};

// Open/Close Sidebar Elements Logic
const openSidebarElement = () => {
  document.querySelector('.header-burger-button')?.classList.add('active');
  document.querySelector('.website-sidebar')?.classList.add('!translate-x-0', 'overflow-y-auto');
};

const closeSidebarElement = () => {
  document.querySelector('.header-burger-button')?.classList.remove('active');
  document.querySelector('.website-sidebar')?.classList.remove('!translate-x-0', 'overflow-y-auto');
};

const openSidebarCalculatorsElement = () => {
  if (window.innerWidth < 1280) {
    document.querySelector('.sidebar-calculators')?.classList.add('!translate-x-0');
    document.querySelector('.header-calculators-toggle-button')?.classList.add('opacity-0', 'pointer-events-none');
  } else {
    if (document.querySelector('.sidebar-calculators')?.classList.contains('xl:translate-x-0')) {
      document.querySelector('.sidebar-calculators')?.classList.add('!-translate-x-full');
    } else {
      document.querySelector('.sidebar-calculators')?.classList.add('!translate-x-0');
    }
  }
};

const closeSidebarCalculatorsElement = () => {
  document.querySelector('.sidebar-calculators')?.classList.remove('!-translate-x-full', '!translate-x-0');
  document.querySelector('.header-calculators-toggle-button')?.classList.remove('opacity-0', 'pointer-events-none');
};

const openSidebarUserBlockElement = () => {
  document
    .querySelector('.sidebar-user-block')
    ?.style.setProperty('height', `${document.querySelector('.sidebar-user-block-content')?.scrollHeight}px`);
  document.querySelector('.sidebar-user-block')?.classList.add('active');
};

const closeSidebarUserBlockElement = () => {
  document.querySelector('.sidebar-user-block')?.style.setProperty('height', '0px');
  document.querySelector('.sidebar-user-block')?.classList.remove('active');
};

// Sidebar logic
const headerBurgerButtonClickHandler = () => {
  const headerBurgerButton = document.querySelector('.header-burger-button');

  headerBurgerButton.addEventListener('click', () => {
    if (headerBurgerButton.classList.contains('active')) {
      closeSidebarElement();
    } else {
      openSidebarElement();
      closeSidebarCalculatorsElement();
    }
  });
};

const sidebarSearchLogic = () => {
  const INPUT_MIN_LENGTH = 3;
  const sidebarSearchElement: HTMLFormElement = document.querySelector('.website-sidebar-search');

  if (sidebarSearchElement !== null) {
    const sidebarSearchInputElement: HTMLInputElement = sidebarSearchElement.querySelector('.sidebar-search-input');
    const sidebarSearchButtonElement: HTMLButtonElement = sidebarSearchElement.querySelector('.sidebar-search-button');

    sidebarSearchButtonElement.addEventListener('click', () => {
      sidebarSearchElement.submit();
      GlobalPreloaderHelper.show();
    });

    if (sidebarSearchInputElement !== null) {
      sidebarSearchInputElement.addEventListener('input', () => {
        if (sidebarSearchInputElement.value.length >= VariablesHelper.SEARCH_INPUT_MIN_LENGTH) {
          sidebarSearchButtonElement.classList.remove('grayscale', 'pointer-events-none');
        } else {
          sidebarSearchButtonElement.classList.add('grayscale', 'pointer-events-none');
        }
      });
    }

    sidebarSearchElement.addEventListener('submit', function (event) {
      event.preventDefault();
      GlobalPreloaderHelper.show();
      sidebarSearchElement.submit();
    });
  }
};

const collapseSidebarLogic = () => {
  const sidebarCollapseButton = document.querySelector('.sidebar-collapse-button');
  const sidebarCollapseButtonIcon = sidebarCollapseButton?.querySelector('.sidebar-collapse-button-icon');
  const websiteSidebarElement = document.querySelector('.website-sidebar');
  const sidebarCalculators = document.querySelector('.sidebar-calculators');
  const pageContent = document.querySelector('#page-content');
  const companyInfoBanner = document.querySelector('.company-info-banner');
  const websiteFooter = document.querySelector('.website-footer');
  const websiteSystemMessage = document.querySelector('.website-system-message');
  const websiteButtonToTop = document.querySelector('.website-button-to-top');

  const showWebsiteSidebarElement = () => {
    sidebarCollapseButton.classList.remove('-translate-x-full', 'rounded-r-none');
    sidebarCollapseButtonIcon.classList.remove('rotate-180');
    websiteSidebarElement.classList.add('xl:translate-x-0');
    sidebarCalculators?.classList.add('xl:translate-x-0');
    pageContent?.classList.add('xl:w-[calc(100%-theme(width.80))]');
    companyInfoBanner?.classList.add('xl:w-[calc(100%-theme(width.80))]');
    websiteFooter?.classList.add('xl:w-[calc(100%-theme(width.80))]');
    websiteSystemMessage?.classList.add('xl:w-[calc(100%-theme(width.80))]');
    websiteButtonToTop?.classList.add('xl:right-80');

    localStorage.setItem('websiteSidebarState', 'visible');
  };

  const hideWebsiteSidebarElement = () => {
    sidebarCollapseButton.classList.add('-translate-x-full', 'rounded-r-none');
    sidebarCollapseButtonIcon.classList.add('rotate-180');
    websiteSidebarElement.classList.remove('xl:translate-x-0');
    sidebarCalculators?.classList.remove('xl:translate-x-0');
    pageContent?.classList.remove('xl:w-[calc(100%-theme(width.80))]');
    companyInfoBanner?.classList.remove('xl:w-[calc(100%-theme(width.80))]');
    websiteFooter?.classList.remove('xl:w-[calc(100%-theme(width.80))]');
    websiteSystemMessage?.classList.remove('xl:w-[calc(100%-theme(width.80))]');
    websiteButtonToTop?.classList.remove('xl:right-80');

    localStorage.setItem('websiteSidebarState', 'hidden');
  };

  if (localStorage.getItem('websiteSidebarState') === 'hidden') {
    hideWebsiteSidebarElement();
  }

  if (sidebarCollapseButton !== null && websiteSidebarElement !== null) {
    sidebarCollapseButton.addEventListener('click', () => {
      closeSidebarCalculatorsElement();
      if (websiteSidebarElement.classList.contains('xl:translate-x-0')) {
        hideWebsiteSidebarElement();
      } else {
        showWebsiteSidebarElement();
      }
    });
  }
};

// Sidebar Calculators Logic
const sidebarCalculatorsVisibilityLogic = () => {
  const sidebarCalculatorsElement = document.querySelector('.sidebar-calculators');

  const sidebarCalculatorsToggleButtons = document.querySelectorAll('.sidebar-calculators-toggle-button');

  if (sidebarCalculatorsToggleButtons.length > 0) {
    sidebarCalculatorsToggleButtons.forEach((button: HTMLElement) => {
      button.addEventListener('click', () => {
        if (sidebarCalculatorsElement.classList.contains('!-translate-x-full') || sidebarCalculatorsElement.classList.contains('!translate-x-0')) {
          closeSidebarCalculatorsElement();
        } else {
          openSidebarCalculatorsElement();
          closeSidebarElement();
        }
      });
    });
  }

  const sidebarCalculatorsCloseButton = document.querySelector('.sidebar-calculators-close-button');
  if (sidebarCalculatorsCloseButton !== null) {
    sidebarCalculatorsCloseButton.addEventListener('click', () => {
      closeSidebarCalculatorsElement();
    });
  }
};

const sidebarCalculatorItemPopButtonClickHandler = () => {
  const sidebarCalculatorsPopButtons = document.querySelectorAll('.sidebar-calculators .calculator-pop-button');

  sidebarCalculatorsPopButtons.forEach((button: HTMLElement) => {
    button.addEventListener('click', () => {
      if (button.classList.contains('inside-draggable-modal')) {
        button.classList.remove('inside-draggable-modal');
        button.children[0].classList.remove('!rotate-[135deg]');
        button.parentElement.classList.remove('inside-draggable-modal');
        const modalId = button.parentElement.parentElement.getAttribute('id');
        document.querySelector('.sidebar-calculators-list').appendChild(button.parentElement);
        document.querySelector(`#${modalId}`).remove();
      } else {
        button.classList.add('inside-draggable-modal');
        button.children[0].classList.add('!rotate-[135deg]');
        button.parentElement.classList.add('inside-draggable-modal');
        const modalId = `${button.parentElement.querySelector('.complex_component').getAttribute('id')}-draggable-modal`;
        const sidebarCalculatorDraggableModal = `<div class="block w-80 fixed top-20 right-96 z-[10003]" draggable="true" id="${modalId}"></div>`;
        document.querySelector('body').insertAdjacentHTML('beforeend', sidebarCalculatorDraggableModal);
        document.querySelector(`#${modalId}`).appendChild(button.parentElement);
        DraggableHelper.dragElement(document.querySelector(`#${modalId}`));
      }
    });
  });
};

// User Auth Logic
const sidebarUserBlockVisibilityLogic = () => {
  const sidebarUserBlockElement = document.querySelector('.sidebar-user-block');

  const sidebarUserBlockToggleButtons = document.querySelectorAll('.sidebar-user-block-toggle-button');

  if (sidebarUserBlockToggleButtons.length > 0) {
    sidebarUserBlockToggleButtons.forEach((button: HTMLElement) => {
      button.addEventListener('click', () => {
        if (window.innerWidth < 1280) {
          if (
            sidebarUserBlockElement.classList.contains('active') &&
            document.querySelector('.website-sidebar')?.classList.contains('!translate-x-0')
          ) {
            closeSidebarUserBlockElement();
          } else {
            openSidebarUserBlockElement();
          }

          closeSidebarCalculatorsElement();
          openSidebarElement();
        } else {
          if (sidebarUserBlockElement.classList.contains('active')) {
            closeSidebarUserBlockElement();
          } else {
            openSidebarUserBlockElement();
          }
        }
      });
    });
  }
};

const authModalLogic = () => {
  const authModalElement = document.querySelector('.auth-modal');

  const authModalOpenButtons = document.querySelectorAll('.auth-modal-open-button');
  if (authModalOpenButtons.length > 0) {
    authModalOpenButtons.forEach((button: HTMLElement) => {
      button.addEventListener('click', () => {
        authModalElement
          ?.querySelector('#user-auth-modal-app-wrapper')
          .setAttribute('data-auth-form-type', button.getAttribute('data-auth-form-type'));
        authModalElement.classList.add('opacity-100', 'pointer-events-auto');
      });
    });
  }

  const authModalCloseElements = ['.auth-modal-backdrop', '.auth-modal-close'];
  authModalCloseElements.forEach((element) => {
    authModalElement?.querySelector(element).addEventListener('click', () => {
      authModalElement.classList.remove('opacity-100', 'pointer-events-auto');
    });
  });
};

const userLogOutLogic = async () => {
  const userLogOutButton = document.querySelector('.user-logout-button');

  if (userLogOutButton !== null) {
    userLogOutButton.addEventListener('click', () => {
      WebsiteApiHelper.postUrlEncoded('/auth/logout/', {}).then(() => {
        window.location.reload();
      });
    });
  }
};

// Accordions Logic
const accordionLogicHandler = () => {
  const accordionsList = document.querySelectorAll('.accordion');

  accordionsList.forEach((accordion) => {
    const accordionHeader = accordion.querySelector('.accordion-header') as HTMLElement;
    const accordionBody = accordion.querySelector('.accordion-body') as HTMLElement;
    const accordionBodyContent = accordion.querySelector('.accordion-body-content') as HTMLElement;

    if (!accordionHeader || !accordionBody || !accordionBodyContent) return;

    const getContentHeight = (element: HTMLElement): number => {
      return element.offsetHeight;
    };

    const updateParentAccordionHeights = (startElement: HTMLElement) => {
      let currentElement = startElement;
      let parentAccordion = currentElement.closest('.accordion')?.parentElement?.closest('.accordion') as HTMLElement | null;

      while (parentAccordion) {
        const parentBody = parentAccordion.querySelector('.accordion-body') as HTMLElement;
        const parentContent = parentAccordion.querySelector('.accordion-body-content') as HTMLElement;

        if (!parentBody || !parentBody.classList.contains('active')) break;

        const parentContentHeight = getContentHeight(parentContent);

        const totalHeight = parentContentHeight;

        parentBody.style.height = `${totalHeight}px`;

        currentElement = parentAccordion;
        parentAccordion = parentAccordion.closest('.accordion')?.parentElement?.closest('.accordion') as HTMLElement | null;
      }
    };

    const initialContentHeight = getContentHeight(accordionBodyContent);

    if (accordionBody.classList.contains('active')) {
      const originalTransition = accordionBody.style.transition;
      accordionBody.style.transition = 'none';
      accordionBody.style.height = `${initialContentHeight}px`;
      void accordionBody.offsetWidth;
      accordionBody.style.transition = originalTransition;

      setTimeout(() => {
        updateParentAccordionHeights(accordion);
      }, 0);
    } else {
      accordionBody.style.height = '0px';
    }

    accordionHeader.addEventListener('click', (event: Event) => {
      if (accordionHeader.classList.contains('open-on-button-click') && !(event.target as HTMLElement).classList.contains('accordion-button')) {
        return;
      }

      const isActive = accordionBody.classList.contains('active');
      accordionHeader.classList.toggle('active', !isActive);
      accordionBody.classList.toggle('active', !isActive);

      if (!isActive) {
        const contentHeight = getContentHeight(accordionBodyContent);

        accordionBody.style.height = `${contentHeight}px`;

        requestAnimationFrame(() => {
          updateParentAccordionHeights(accordion);
        });
      } else {
        requestAnimationFrame(() => {
          accordionBody.style.height = '0px';
          updateParentAccordionHeights(accordion);
        });
      }
    });

    const contentObserver = new MutationObserver(() => {
      if (accordionBody.classList.contains('active')) {
        const contentHeight = getContentHeight(accordionBodyContent);
        accordionBody.style.height = `${contentHeight}px`;

        requestAnimationFrame(() => {
          updateParentAccordionHeights(accordion);
        });
      }
    });

    contentObserver.observe(accordionBodyContent, {
      childList: true,
      subtree: true,
      attributes: true,
      characterData: true,
    });

    const resizeObserver = new ResizeObserver(() => {
      if (accordionBody.classList.contains('active')) {
        const contentHeight = getContentHeight(accordionBodyContent);
        accordionBody.style.height = `${contentHeight}px`;

        requestAnimationFrame(() => {
          updateParentAccordionHeights(accordion);
        });
      }
    });

    resizeObserver.observe(accordionBodyContent);
  });
};

// Playing videos with autoplay attribute
const playingVideoWithAutoplayAttribute = () => {
  const videosList = document.querySelectorAll('video');
  videosList.forEach((video: HTMLVideoElement) => {
    if (video.hasAttribute('autoplay')) {
      video.addEventListener('canplaythrough', () => {
        video.pause();
        setTimeout(() => {
          video.play();
        }, 100);
      });
    }
  });
};

// On scroll handler
const onScrollHandler = () => {
  buttonToTopOnScrollHandler();
};

// Back button logic
const backButtonEventHandler = () => {
  window.addEventListener('pageshow', (event) => {
    if (event.persisted) {
      const globalPreloaderElement = document.querySelector('#global_preloader');
      if (globalPreloaderElement !== null && globalPreloaderElement.classList.contains('visible')) {
        GlobalPreloaderHelper.hide();
      }
    }
  });
};
backButtonEventHandler();

// Page tools logic
const downloadAsPDFLinksLogic = () => {
  const downloadAsPDFLinks = document.querySelectorAll('.download-as-pdf-link');

  downloadAsPDFLinks.forEach((link: HTMLAnchorElement) => {
    link.addEventListener('click', async (event) => {
      event.preventDefault();

      GlobalPreloaderHelper.show();

      try {
        const url = link.getAttribute('href');
        const response = await fetch(url);

        if (!response.ok) throw new Error('Network response was not ok');

        const blob = await response.blob();
        const blobUrl = URL.createObjectURL(blob);

        const tempLink = document.createElement('a');
        tempLink.href = blobUrl;
        tempLink.download = document.title + '.pdf';
        document.body.appendChild(tempLink);
        tempLink.click();
        document.body.removeChild(tempLink);
        URL.revokeObjectURL(blobUrl);
      } catch (error) {
        console.error('Failed to download PDF:', error);
      } finally {
        GlobalPreloaderHelper.hide();
      }
    });
  });
};

const websiteSystemMessagesLogic = () => {
  const websiteSystemMessageElement = document.querySelector('.website-system-message');

  if (websiteSystemMessageElement !== null) {
    setTimeout(() => {
      websiteSystemMessageElement.classList.add('hidden');
    }, 10000);

    const closeButton = websiteSystemMessageElement.querySelector('.close-website-system-message');
    closeButton.addEventListener('click', () => {
      websiteSystemMessageElement.classList.add('hidden');
    });
  }
};

onMounted(() => {
  initButtonToTopElement();
  buttonToTopClickHandler();

  headerBurgerButtonClickHandler();
  sidebarSearchLogic();
  collapseSidebarLogic();
  sidebarCalculatorsVisibilityLogic();
  sidebarCalculatorItemPopButtonClickHandler();

  sidebarUserBlockVisibilityLogic();
  authModalLogic();
  userLogOutLogic();

  accordionLogicHandler();

  playingVideoWithAutoplayAttribute();

  downloadAsPDFLinksLogic();

  websiteSystemMessagesLogic();

  GlobalHelper.dynamicModalLogic();

  AnalyticsHelper.amplitudePageView();
  AnalyticsHelper.clarityPageView();

  onScrollHandler();
  window.addEventListener('scroll', onScrollHandler);
});
</script>
